/**
 * RevealedImage — LQIP placeholder + final image with a tasteful fade-in.
 *
 * The component:
 *  - Renders a `<picture>` with AVIF + WebP + PNG sources and a `<sizes>`
 *    attribute so the browser picks the smallest variant that fits.
 *  - Draws the 16x16 base64 LQIP from `src/generated/lqips.ts` as a
 *    blurred backdrop the moment the card is composited, so the card
 *    never reads as "empty" while the full image loads.
 *  - Fades the full image in over ~280ms once it finishes decoding (CSS
 *    transition — playable on any browser, avoids motion's stricter
 *    style typing). Reduced-motion mode skips the fade.
 *  - Always sets `width` and `height` (or `aspect-ratio` via CSS) so the
 *    image's footprint is reserved BEFORE bytes arrive — no layout shift.
 *
 * The companion `AtmosphereSurface` produces the same LQIP-then-fade
 * choreography for `background-image` surfaces (atmosphere imagery,
 * category cards) — drop it inside an existing positioned wrapper that
 * already carries the layout/transform CSS.
 */
import { type CSSProperties, type ImgHTMLAttributes, useMemo } from 'react';

import { lqipFor } from '@/generated/lqips';

import { prefersReducedMotion } from './timings';
import { useImageLoadRef } from './useImageLoad';

/** ~280ms fade-in choreographed with the card entrance spring. */
const FADE_MS = 280;

export interface PictureSource {
  /** MIME type — typically `image/avif` or `image/webp`. */
  type: string;
  /** Full srcSet string. */
  srcSet: string;
}

export interface RevealedImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'> {
  /** The canonical PNG path; used as fallback `<img src>` and as the LQIP key. */
  src: string;
  /** Optional AVIF / WebP / size-variant sources. */
  sources?: readonly PictureSource[];
  /** `<img sizes>` value — defaults to 100vw. */
  sizes?: string;
  /** Intrinsic width / height (mandatory: kills layout shift). */
  width: number;
  height: number;
  /** Tag the wrapping `<picture>` with a class for layout-specific tuning. */
  className?: string;
  /** Style applied to the `<picture>` wrapper. */
  style?: CSSProperties;
  /** Alt text — pass `''` for purely decorative imagery. */
  alt: string;
  /** Force eager loading (above-the-fold hero / first-row thumbnails). */
  eager?: boolean;
}

/**
 * Render a `<picture>` whose LQIP-blurred backdrop is visible until the
 * full image loads, then fades the full image in.
 */
export function RevealedImage({
  src,
  sources,
  sizes = '100vw',
  width,
  height,
  className,
  style,
  alt,
  eager = false,
  ...imgProps
}: RevealedImageProps): React.JSX.Element {
  const { ref: imgRef, loaded } = useImageLoadRef(src);
  const reduced = prefersReducedMotion();
  const lqip = lqipFor(src);

  const wrapperStyle: CSSProperties = useMemo(
    () => ({
      position: 'relative',
      display: 'block',
      overflow: 'hidden',
      width: '100%',
      aspectRatio: `${width} / ${height}`,
      ...style,
    }),
    [width, height, style],
  );

  const baseLayer: CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  return (
    <picture className={className} style={wrapperStyle}>
      {lqip && (
        <span
          aria-hidden="true"
          style={{
            ...baseLayer,
            backgroundImage: `url(${lqip})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(18px) saturate(1.1)',
            transform: 'scale(1.08)', // mask blur edges
            opacity: loaded && !reduced ? 0 : 1,
            transition: reduced ? 'none' : `opacity ${FADE_MS}ms ease-out`,
          }}
        />
      )}
      {sources?.map((s, i) => (
        <source key={i} type={s.type} srcSet={s.srcSet} sizes={sizes} />
      ))}
      <img
        {...imgProps}
        ref={imgRef}
        src={src}
        width={width}
        height={height}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={eager ? 'high' : 'auto'}
        style={{
          ...baseLayer,
          opacity: loaded || !lqip ? 1 : 0,
          transition: reduced ? 'none' : `opacity ${FADE_MS}ms ease-out`,
        }}
      />
    </picture>
  );
}

// ---------------------------------------------------------------------------
// AtmosphereSurface — slot-in <picture> for full-bleed atmosphere imagery
// inside an existing positioned wrapper (so existing hub CSS keeps working).
// ---------------------------------------------------------------------------

export interface AtmosphereSurfaceProps {
  /** Canonical PNG path. */
  src: string;
  /** Optional AVIF / WebP modern formats. */
  avifSrc?: string;
  webpSrc?: string;
  /** Optional responsive width variants in AVIF / WebP. */
  avifSrcSet?: string;
  webpSrcSet?: string;
  /** Optional `<img sizes>` value. */
  sizes?: string;
  /** Eager-load (above-the-fold atmospheres should be eager). */
  eager?: boolean;
  /** Object position — default `center`. */
  position?: string;
}

/**
 * The element a caller drops inside an existing CSS wrapper (the desk,
 * the category card, the field) to replace a `background-image: url(...)`
 * with a LQIP-blurred-then-faded `<picture>`. Caller controls positioning
 * via the wrapper's own CSS rules — we just paint the layers.
 */
export function AtmosphereSurface({
  src,
  avifSrc,
  webpSrc,
  avifSrcSet,
  webpSrcSet,
  sizes,
  eager = false,
  position = 'center',
}: AtmosphereSurfaceProps): React.JSX.Element {
  const { ref: imgRef, loaded } = useImageLoadRef(src);
  const reduced = prefersReducedMotion();
  const lqip = lqipFor(src);

  return (
    <>
      {lqip && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${lqip})`,
            backgroundSize: 'cover',
            backgroundPosition: position,
            filter: 'blur(22px) saturate(1.05)',
            transform: 'scale(1.1)',
            zIndex: 0,
            opacity: loaded && !reduced ? 0 : 1,
            transition: reduced ? 'none' : `opacity ${FADE_MS}ms ease-out`,
          }}
        />
      )}
      <picture>
        {avifSrcSet && (
          <source type="image/avif" srcSet={avifSrcSet} {...(sizes ? { sizes } : {})} />
        )}
        {webpSrcSet && (
          <source type="image/webp" srcSet={webpSrcSet} {...(sizes ? { sizes } : {})} />
        )}
        {avifSrc && !avifSrcSet && <source type="image/avif" srcSet={avifSrc} />}
        {webpSrc && !webpSrcSet && <source type="image/webp" srcSet={webpSrc} />}
        <img
          ref={imgRef}
          src={src}
          alt=""
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={eager ? 'high' : 'auto'}
          style={{
            objectPosition: position,
            opacity: loaded || !lqip ? 1 : 0,
            transition: reduced ? 'none' : `opacity ${FADE_MS}ms ease-out`,
          }}
        />
      </picture>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers for building variant URL paths
// ---------------------------------------------------------------------------

/**
 * Given a canonical PNG path (e.g. `/generated/games-hub/thumb-trivia-light.png`),
 * synthesize the modern-format srcSet (e.g. AVIF) at the listed widths.
 * Returns an empty string if `widths` is empty.
 */
export function buildSrcSet(
  canonicalPng: string,
  format: 'avif' | 'webp',
  widths: readonly number[],
): string {
  if (widths.length === 0) return '';
  const stem = canonicalPng.replace(/\.png$/, '');
  return widths.map((w) => `${stem}-${w}w.${format} ${w}w`).join(', ');
}

/**
 * Full-size sidecar variant for a canonical PNG path:
 *   `/generated/.../foo.png`  ->  `/generated/.../foo.avif`
 */
export function variantOf(canonicalPng: string, format: 'avif' | 'webp'): string {
  return canonicalPng.replace(/\.png$/, `.${format}`);
}
